import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  MathUtils,
  Points,
  ShaderMaterial,
  Vector2,
  type Color,
  type Object3D,
  type WebGLRenderer,
} from 'three'

import {
  SPARK_COUNT,
  SPARK_GRAVITY,
  SPARK_LIFE_MAX_S,
  SPARK_LIFE_MIN_S,
  SPARK_MAX_POINT_PX,
  SPARK_OUTWARD_MAX,
  SPARK_OUTWARD_MIN,
  SPARK_RISE_MAX,
  SPARK_RISE_MIN,
  SPARK_SEED,
  SPARK_SIZE_MAX_RATIO,
  SPARK_SIZE_MIN_RATIO,
  SPARK_STAGGER_SECONDS,
} from '../config'

/**
 * The motes of light that jump off the floor when the model lands.
 *
 * `Points` + a ShaderMaterial rather than `PointsMaterial`, decided on
 * expressiveness rather than speed - 52 vertices is free either way:
 *
 *  - `PointsMaterial.size` is a single material-wide value. Fifty-two identical
 *    dots read as "dots"; the variation in size and lifetime IS the sparkle.
 *  - `gl_PointSize` can be clamped here. Apple GPUs commonly cap
 *    ALIASED_POINT_SIZE_RANGE at 64 and clamp past it silently, so a close
 *    placement would look different on iOS than anywhere else. Clamping
 *    ourselves also puts a hard ceiling on the burst's worst-case fill.
 *  - The round mask comes from `gl_PointCoord`, so there is no texture to ship.
 *
 * No `discard`: on tile-based deferred GPUs a shader containing one disables
 * early-Z for the whole draw call, and under additive blending an alpha of zero
 * is already an exact no-op. The mask is a smoothstep instead.
 *
 * Trajectories are evaluated in closed form from `uTime`, never integrated.
 * That makes one `step(0.5)` identical to thirty `step(1/60)` - which is what
 * lets /debug.html verify the curve deterministically - and means a dropped
 * frame skips ahead instead of drifting off the arc.
 *
 * NOTE: `MathUtils.seededRandom` keeps its seed in a module-global. This is
 * currently its only user in the repo, and the fixed seed below is what makes
 * the Nth placement of a session look the same every session. A second user
 * would break that for both.
 */

export interface SparkBurst {
  /** Parent this wherever the effect lives. */
  readonly object: Object3D
  /** Seconds since the burst began. Negative means "not started yet". */
  setElapsed(seconds: number): void
  /** Draws a fresh distribution. Called on every placement. */
  reseed(): void
  dispose(): void
}

export interface SparkBurstOptions {
  /** Needed for gl_PointSize: the drawing buffer height is read every frame. */
  readonly renderer: WebGLRenderer
  /** Radius of the emission disc in metres. Every other quantity scales off it. */
  readonly spreadMeters: number
  /** Shared with the ring. Already in the renderer's working colour space. */
  readonly color: Color
}

const vertexShader = /* glsl */ `
attribute vec3 aVelocity;
attribute vec2 aTiming;   // x = birth delay (s), y = lifetime (s)
attribute float aSize;

uniform float uTime;
uniform float uGravity;
uniform float uHeightScale;
uniform float uMaxPointSize;

varying float vAlpha;

void main() {
  float t = uTime - aTiming.x;
  float life = clamp(t / aTiming.y, 0.0, 1.0);
  float alive = step(0.0, t) * step(t, aTiming.y);

  // Closed form, not integrated.
  vec3 displaced = position + aVelocity * t + vec3(0.0, 0.5 * uGravity * t * t, 0.0);
  vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
  gl_Position = projectionMatrix * mvPosition;

  // Fast attack, long tail. Multiplied by alive so a spark that is not yet born
  // or already dead contributes exactly nothing under additive blending,
  // whatever the driver does with a zero point size.
  vAlpha = alive * smoothstep(0.0, 0.12, life) * pow(1.0 - life, 1.5);

  // Matches PointsMaterial's convention exactly - it computes
  // size * pixelRatio * (cssHeight / 2) / -z, and cssHeight * pixelRatio is the
  // drawing buffer height - so aSize stays comparable with any three.js
  // Points example. max() guards a spark that ends up behind the eye.
  float projected = aSize * uHeightScale / max(-mvPosition.z, 0.01);
  gl_PointSize = alive * clamp(projected, 1.0, uMaxPointSize);
}
`

const fragmentShader = /* glsl */ `
uniform vec3 uColor;
varying float vAlpha;

void main() {
  // Round sparks with no texture and no discard. gl_PointCoord's flipped Y is
  // irrelevant for a radially symmetric mask.
  float d = length(gl_PointCoord - 0.5);
  gl_FragColor = vec4(uColor, vAlpha * smoothstep(0.5, 0.12, d));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`

/** Latest moment any spark can still be alive. Mirrors EFFECT_SPARK_SECONDS. */
const LAST_BEAT_SECONDS = SPARK_STAGGER_SECONDS + SPARK_LIFE_MAX_S

export const createSparkBurst = ({
  renderer,
  spreadMeters,
  color,
}: SparkBurstOptions): SparkBurst => {
  const positions = new Float32Array(SPARK_COUNT * 3)
  const velocities = new Float32Array(SPARK_COUNT * 3)
  const timings = new Float32Array(SPARK_COUNT * 2)
  const sizes = new Float32Array(SPARK_COUNT)

  const positionAttribute = new BufferAttribute(positions, 3)
  const velocityAttribute = new BufferAttribute(velocities, 3)
  const timingAttribute = new BufferAttribute(timings, 2)
  const sizeAttribute = new BufferAttribute(sizes, 1)

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', positionAttribute)
  geometry.setAttribute('aVelocity', velocityAttribute)
  geometry.setAttribute('aTiming', timingAttribute)
  geometry.setAttribute('aSize', sizeAttribute)

  const uniforms = {
    uColor: {value: color},
    uTime: {value: 0},
    // Expressed per spawn radius, so the burst is self-similar: a 0.3 m figurine
    // and a 3 m mascot get the same silhouette over the same 0.75 s with no
    // per-model tuning.
    uGravity: {value: spreadMeters * SPARK_GRAVITY},
    uHeightScale: {value: 0},
    uMaxPointSize: {value: SPARK_MAX_POINT_PX},
  }

  const material = new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
  })

  const points = new Points(geometry, material)
  points.name = 'entrance-sparks'
  // The bounding sphere is computed from the spawn disc, but the vertex shader
  // displaces the sparks well above it. A camera framing only the top of the
  // burst would cull the entire object - the same trap normalize.ts avoids on
  // SkinnedMesh, for the same reason.
  points.frustumCulled = false
  // Top of the stack: shadow 0, lead-in prints 1, ring 2, these.
  points.renderOrder = 3
  points.visible = false

  // Fixing the seed once here (rather than per reseed) is deliberate: successive
  // placements look different from each other, but the sequence is identical
  // from session to session, so a screenshot comparison actually means something.
  MathUtils.seededRandom(SPARK_SEED)

  const bufferSize = new Vector2()

  const reseed = (): void => {
    for (let i = 0; i < SPARK_COUNT; i++) {
      const angle = MathUtils.seededRandom() * Math.PI * 2
      // sqrt() makes the draw uniform over the disc rather than bunched at the
      // centre.
      const radius = spreadMeters * Math.sqrt(MathUtils.seededRandom())
      const cos = Math.cos(angle)
      const sin = Math.sin(angle)

      positions[i * 3] = cos * radius
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = sin * radius

      const rise = spreadMeters * MathUtils.randFloat(SPARK_RISE_MIN, SPARK_RISE_MAX)
      const outward = spreadMeters * MathUtils.randFloat(SPARK_OUTWARD_MIN, SPARK_OUTWARD_MAX)
      velocities[i * 3] = cos * outward
      velocities[i * 3 + 1] = rise
      velocities[i * 3 + 2] = sin * outward

      timings[i * 2] = MathUtils.randFloat(0, SPARK_STAGGER_SECONDS)
      timings[i * 2 + 1] = MathUtils.randFloat(SPARK_LIFE_MIN_S, SPARK_LIFE_MAX_S)

      sizes[i] = spreadMeters * MathUtils.randFloat(SPARK_SIZE_MIN_RATIO, SPARK_SIZE_MAX_RATIO)
    }

    positionAttribute.needsUpdate = true
    velocityAttribute.needsUpdate = true
    timingAttribute.needsUpdate = true
    sizeAttribute.needsUpdate = true
  }

  reseed()

  return {
    object: points,
    setElapsed: (seconds) => {
      if (seconds < 0 || seconds > LAST_BEAT_SECONDS) {
        points.visible = false
        return
      }
      points.visible = true
      uniforms.uTime.value = seconds
      // Read every frame rather than cached: FullWindowCanvas rebuilds the
      // canvas on orientation change, and calling setSize/setPixelRatio to find
      // out would fight it.
      uniforms.uHeightScale.value = renderer.getDrawingBufferSize(bufferSize).y * 0.5
    },
    reseed,
    dispose: () => {
      points.removeFromParent()
      geometry.dispose()
      material.dispose()
    },
  }
}
